import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  MenuItem,
  Stack,
  Box,
  Typography,
  CircularProgress,
  Alert,
} from "@mui/material";
import useApiCall from "../../../hooks/useApiCall";
import { INDIGO, SLATE } from "../../../constants/Colors";

const FIELD_LABEL_SX = {
  fontSize: "0.8125rem",
  fontWeight: 500,
  color: SLATE[700],
  mb: "6px",
  display: "block",
};

const FIELD_HINT_SX = {
  fontSize: "0.75rem",
  color: SLATE[400],
  mb: "6px",
  display: "block",
};

const INPUT_SX = {
  "& .MuiOutlinedInput-root": {
    borderRadius: "8px",
    fontSize: "0.875rem",
    bgcolor: "#ffffff",
    "& fieldset": { borderColor: SLATE[300] },
    "&:hover fieldset": { borderColor: SLATE[400] },
    "&.Mui-focused fieldset": { borderColor: INDIGO[500] },
  },
  "& .MuiInputBase-input": { color: SLATE[900] },
};

export default function CloneTemplateDialog({ open, template, onClose, onSuccess }) {
  const apps = useSelector((state) => state?.apps.list || []);
  const tokens = useSelector((state) => state?.tokens.value);
  const { makeRequest } = useApiCall();

  const [formData, setFormData] = useState({
    app_id: "",
    site_code: "",
    instance_name: "",
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (formData.app_id && template) {
      const selectedApp = apps.find((app) => app.id === formData.app_id);
      if (selectedApp) {
        setFormData((prev) => ({
          ...prev,
          instance_name: `${template.name} - ${selectedApp.client_name}`,
        }));
      }
    }
  }, [formData.app_id, template, apps]);

  useEffect(() => {
    if (!open) {
      setFormData({ app_id: "", site_code: "", instance_name: "" });
      setError(null);
    }
  }, [open]);

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async () => {
    if (!formData.app_id) { setError("Please select a client"); return; }
    if (!formData.site_code) { setError("Please enter a site code"); return; }

    setLoading(true);
    setError(null);

    try {
      const cloneData = {
        app_id: formData.app_id,
        site_code: formData.site_code,
        overrides: formData.instance_name ? { name: formData.instance_name } : {},
      };

      await makeRequest(`/v2/agent_templates/${template.id}/clone`, {
        method: "POST",
        body: cloneData,
        authTokens: tokens,
      });

      if (onSuccess) onSuccess();
    } catch (err) {
      console.error("Error cloning template:", err);
      setError(err.response?.data?.error || err.message || "Failed to clone template. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  if (!template) return null;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ fontSize: "1rem", fontWeight: 600, color: SLATE[900], pb: 1 }}>
        Clone Template: {template.name}
      </DialogTitle>
      <DialogContent>
        <Stack spacing={2.5} sx={{ mt: 1 }}>
          {error && <Alert severity="error" sx={{ fontSize: "0.875rem" }}>{error}</Alert>}

          <Box>
            <Typography component="label" sx={FIELD_LABEL_SX}>
              Select Client <Box component="span" sx={{ color: "#ef4444" }}>*</Box>
            </Typography>
            <TextField
              select
              value={formData.app_id}
              onChange={(e) => handleInputChange("app_id", e.target.value)}
              disabled={loading}
              fullWidth
              size="small"
              sx={INPUT_SX}
            >
              <MenuItem value="">Select a client</MenuItem>
              {apps.map((app) => (
                <MenuItem key={app.id} value={app.id}>
                  {app.client_name} ({app.url_code})
                </MenuItem>
              ))}
            </TextField>
          </Box>

          <Box>
            <Typography component="label" sx={FIELD_LABEL_SX}>
              Site Code <Box component="span" sx={{ color: "#ef4444" }}>*</Box>
            </Typography>
            <Typography sx={FIELD_HINT_SX}>Unique identifier for this agent instance within the client</Typography>
            <TextField
              value={formData.site_code}
              onChange={(e) => handleInputChange("site_code", e.target.value)}
              disabled={loading}
              placeholder="e.g., main-site, store-1"
              fullWidth
              size="small"
              sx={INPUT_SX}
            />
          </Box>

          <Box>
            <Typography component="label" sx={FIELD_LABEL_SX}>Instance Name (Optional)</Typography>
            <Typography sx={FIELD_HINT_SX}>Leave empty to auto-generate based on template and client name</Typography>
            <TextField
              value={formData.instance_name}
              onChange={(e) => handleInputChange("instance_name", e.target.value)}
              disabled={loading}
              fullWidth
              size="small"
              sx={INPUT_SX}
            />
          </Box>
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
        <Button
          onClick={onClose}
          disabled={loading}
          variant="outlined"
          size="small"
          sx={{ borderColor: SLATE[300], color: SLATE[700], textTransform: "none" }}
        >
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          size="small"
          disabled={loading || !formData.app_id || !formData.site_code}
          sx={{ textTransform: "none", fontSize: "0.875rem", fontWeight: 500, boxShadow: "none", "&:hover": { boxShadow: "none" } }}
        >
          {loading ? <CircularProgress size={16} sx={{ color: "#fff" }} /> : "Clone Template"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
